import type {
  PageSize,
  PartnerStatement,
  ReportConfigBody,
  ReportKind,
  StatementKind,
  StatementSide,
} from '@palaccount/contracts';
import { DEFAULT_PAGE_SIZE } from '@palaccount/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api';
import { throwApiError } from '@/lib/server-messages';

/** DSH-05..08 data layer + the R13 saved-configuration CRUD. */

const KEY = ['reports'] as const;

/**
 * Every filter, preset and typed threshold on a report screen is part of the
 * query key, so without this each keystroke would drop the page back to its
 * loading skeleton — closing the detail fold and losing the reader's place.
 * Holding the previous answer while the new one loads is what BR-DSH-10-07
 * ("changing any filter recomputes every card, chart and table") should feel
 * like: the figures update, the page does not blink.
 */
const KEEP_PREVIOUS = { placeholderData: keepPreviousData } as const;

export function useSalesReport(query: { from: string; to: string; salespersonId?: string; customerId?: string; categoryId?: string; warehouseId?: string }) {
  return useQuery({
    queryKey: [...KEY, 'sales', query],
    queryFn: async () => {
      const res = await api.reports.sales({ query });
      if (res.status !== 200) throwApiError(res.status, res.body);
      return res.body;
    },
    ...KEEP_PREVIOUS,
  });
}

export function usePurchaseReport(query: { from: string; to: string; supplierId?: string; categoryId?: string; warehouseId?: string; purchasedById?: string }) {
  return useQuery({
    queryKey: [...KEY, 'purchases', query],
    queryFn: async () => {
      const res = await api.reports.purchases({ query });
      if (res.status !== 200) throwApiError(res.status, res.body);
      return res.body;
    },
    ...KEEP_PREVIOUS,
  });
}

/**
 * DSH-06 §5h. The four §5a filters ride along with the window and the item —
 * BR-DSH-10-07: "changing any filter must recompute every card, chart and
 * table on the page at once", and this feeds a chart on that page. They are
 * part of the KEY as well as the request, so changing a filter refetches
 * rather than showing the previous filter's drift under the new one's label.
 */
export function usePriceTrend(
  query:
    | {
        from: string;
        to: string;
        itemId: string;
        supplierId?: string | undefined;
        categoryId?: string | undefined;
        warehouseId?: string | undefined;
        purchasedById?: string | undefined;
      }
    | null,
) {
  return useQuery({
    queryKey: [...KEY, 'price-trend', query],
    enabled: query !== null,
    queryFn: async () => {
      const res = await api.reports.purchasePriceTrend({ query: query! });
      if (res.status !== 200) throwApiError(res.status, res.body);
      return res.body;
    },
  });
}

export function useInventoryReport(query: { warehouseId?: string; categoryId?: string; threshold?: number }) {
  return useQuery({
    queryKey: [...KEY, 'inventory', query],
    queryFn: async () => {
      const res = await api.reports.inventory({ query });
      if (res.status !== 200) throwApiError(res.status, res.body);
      return res.body;
    },
    ...KEEP_PREVIOUS,
  });
}

export function useFinancialReport(query: {
  from: string;
  to: string;
  statement: StatementKind;
  compare: 'previous' | 'lastYear' | 'custom' | 'none';
  compareFrom?: string;
  compareTo?: string;
}) {
  return useQuery({
    queryKey: [...KEY, 'financial', query],
    queryFn: async () => {
      const res = await api.reports.financial({ query });
      if (res.status !== 200) throwApiError(res.status, res.body);
      return res.body;
    },
    ...KEEP_PREVIOUS,
  });
}

/**
 * CRM-03 — the statement report's query. Every control is a QUERY PARAMETER
 * for the reason `usePartnerStatement` gives: the cards and the bands describe
 * the whole ledger (BR-CRM-04-13), so a browser-side filter would leave them
 * disagreeing with the rows. `null` = no partner picked yet, nothing asked.
 */
export interface StatementReportQuery {
  partnerId: string;
  side?: StatementSide | undefined;
  from?: string | undefined;
  to?: string | undefined;
  q?: string | undefined;
  /** Which currency's statement; absent = the partner's own. */
  currency?: string | undefined;
}

export function useStatementReport(
  query: StatementReportQuery | null,
  page = 1,
  /** The unbounded opt-out is taken by ONE caller — the printable ledger on the page. */
  pageSize: PageSize = DEFAULT_PAGE_SIZE,
) {
  return useQuery({
    queryKey: [...KEY, 'statement', query, page, pageSize],
    enabled: query !== null,
    queryFn: async (): Promise<PartnerStatement> => {
      const res = await api.reports.partnerStatement({
        query: {
          partnerId: query!.partnerId,
          ...(query!.side ? { side: query!.side } : {}),
          ...(query!.from ? { from: query!.from } : {}),
          ...(query!.to ? { to: query!.to } : {}),
          ...(query!.q ? { q: query!.q } : {}),
          ...(query!.currency ? { currency: query!.currency } : {}),
          page,
          pageSize,
        },
      });
      if (res.status !== 200) throwApiError(res.status, res.body);
      return res.body;
    },
    ...KEEP_PREVIOUS,
  });
}

export function useReportConfigs(report: ReportKind) {
  return useQuery({
    queryKey: [...KEY, 'configs', report],
    queryFn: async () => {
      const res = await api.reports.listConfigs({ query: { report } });
      if (res.status !== 200) throwApiError(res.status, res.body);
      return res.body.configs;
    },
  });
}

export function useSaveConfig(report: ReportKind) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; config: ReportConfigBody }) => {
      const res = await api.reports.createConfig({ body: { report, ...input } });
      if (res.status !== 201) throwApiError(res.status, res.body);
      return res.body;
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: [...KEY, 'configs', report] }),
  });
}

export function useDeleteConfig(report: ReportKind) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (configId: string) => {
      const res = await api.reports.deleteConfig({ params: { configId } });
      if (res.status !== 204) throwApiError(res.status, res.body);
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: [...KEY, 'configs', report] }),
  });
}
